import { useState, useEffect } from "react";
import Link from "next/link";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import ChevronRight from "@mui/icons-material/ChevronRightRounded";
import { Carousel } from "@atoms/Carousel";
import ListBoxItem from "@atoms/ListBoxItem";
import { Section } from "@atoms/Section";
import { WideCard } from "@atoms/WideCard";
import color from "@definitions/mui/color";
import DynamicImage from "@ds/components/DynamicImage";
import Text from "@ds/components/Text";
import type { TProductIconData } from "@layouts/Main/lib/Header/lib/MenuProduct/type";
import { formattedDate } from "@helpers/dateTime";
import useResponsive from "@helpers/useResponsive";
import { InsightData } from "@repositories/insight/insight.redux";
import useStyles from "../styles";
import dynamic from "next/dynamic";
import { useLanguage } from "@utils/useLanguage";
import Divider from "@mui/material/Divider";

const Header = dynamic(() => import("@layouts/Main/lib/Header"));
const Footer = dynamic(() => import("@layouts/Footer"));
const BreadcrumbsComponent = dynamic(() => import("@atoms/Breadcrumbs/Breadcrumbs"));
const LeftIcon = dynamic(() => import("@icons/mobile").then((mod) => mod.LeftIcon));
const ShareOutlineIcon = dynamic(() => import("@icons/mobile").then((mod) => mod.ShareOutlineIcon));

export interface InsightLiteDetailContainer {
  data: InsightData;
  dataNavbar: TProductIconData[];
  insightList: {
    data: InsightData[];
  };
}

export const InsightLiteDetailContainer: React.FC<InsightLiteDetailContainer> = ({
  data,
  dataNavbar,
  insightList,
}) => {
  const { title, productSuggestion } = data;
  const router = useRouter();
  const isMobile = useResponsive("md");
  const isSmallPC = useResponsive("lg");
  const classes = useStyles({ isVisible: true, isMobile });
  const isHaveProducts = productSuggestion?.length > 0;
  const { t } = useTranslation("common");
  const [scrolled, setScrolled] = useState(false);
  const { language } = useLanguage();

  const relatedArticle = (
    <>
      {insightList?.data.map((item) => (
        <WideCard
          title={item.title}
          description={item.story}
          image={item.imageUrl.mediaPath + "?tr=w-400"}
          date={formattedDate(item.date)}
          subtitle={item.category}
          linkTo={`/insight/${item.slug}`}
        />
      ))}
    </>
  );

  const handleScroll = () => {
    const offset = window.scrollY;
    if (offset > 0) {
      setScrolled(true);
    } else {
      setScrolled(false);
    }
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
  });

  const getCategoryID = (categoryId: string) => {
    let categoryArticle = '';

    switch(categoryId){
      case 'business_research':
        categoryArticle = 'Business Research';
        break;
      case 'success_story':
        categoryArticle = 'Success Story';
        break;
      case 'article':
      default:
        categoryArticle = 'article';
    }

    return categoryArticle;
  };

  return (
    <>
      {isMobile ? null : <Header />}
      <Box className={classes.base}>
        <Box
          my={isMobile ? 0 : "8px"}
          mx={isMobile ? 0 : isSmallPC ? "16px" : 0}
          py={isMobile ? 0 : 1}
          px={isMobile ? 2 : "unset"}
          position="relative"
          zIndex={1}
          bgcolor={isMobile ? color.white : color.grey.mid}
          className={scrolled ? classes.stickyHeader : ""}
        >
          <Container maxWidth="lg" disableGutters>
            {isMobile ? (
              <Box height="56px" display="flex" alignItems="center" justifyContent="space-between">
                <Box display="flex" alignItems="center" sx={{ svg: { marginRight: "20px" } }}>
                  {/* <Link href="/insight"> still looking for approval, but mas Fikry needs this */}
                  <LeftIcon onClick={() => router.back()} />
                  {/* </Link> */}
                  <Text variant="h3">
                    {language === "id" ? `Detail ${data.category}` : `${data.category} Details`}
                  </Text>
                </Box>
                <ShareOutlineIcon />
              </Box>
            ) : (
              <BreadcrumbsComponent
                homeUrl={{ label: t("root"), productPath: "/" }}
                breadcrumbs={[{ label: "Insight", productPath: "/insight" }, { label: data.category, productPath: `/insight/list?category=${getCategoryID(data.categoryId)}` }, { label: title }]}
              />
            )}
          </Container>
        </Box>
        <Box
          bgcolor="white"
          pt={isMobile ? "16px" : "20px"}
          pb={isMobile ? "16px" : "100px"}
          px={isSmallPC ? "16px" : 0}
        >
          <Container maxWidth="lg" disableGutters>
            <Grid container spacing={isMobile ? 0 : 5}>
              <Grid item xs={12} lg={7}>
                <Text color="general-main" variant={isMobile ? "body1" : "h1"} weight="700">
                  {data.title}
                </Text>
                <Box display="flex" alignItems="center" mt={2} mb={3}>
                  <Chip label={data.category} className={classes.category} />
                  <Text
                    className={classes.date}
                    variant={isMobile ? "body2" : "inherit"}
                    weight={isMobile ? "400" : "600"}
                  >
                    {formattedDate(data.date)}
                  </Text>
                </Box>
                <DynamicImage
                  src={data.imageUrl?.mediaPath + "?tr=w-680"}
                  alt={data.title}
                  mb={3}
                  priority
                />
                <Box
                  className={classes.content}
                  dangerouslySetInnerHTML={{ __html: data.story || "" }}
                />
                {isHaveProducts && (
                  <Box mt={4}>
                    <Text weight="700" variant="h3">
                      {t("insight.product")}
                    </Text>
                    <Section pt={3} pb={3} px={0} maxWidth="full-width">
                      <Carousel
                        settings={{
                          slidesToShow: isMobile ? productSuggestion?.length : 2,
                          variableWidth: true,
                        }}
                        centerWhenLessThenLimit={false}
                      >
                        {productSuggestion?.map((item, key) => {
                          return (
                            <Box key={key} py={1} px={1}>
                              <ListBoxItem
                                width={isMobile ? "100%" : 280}
                                height={isMobile ? "auto" : 180}
                                minHeight={isMobile ? 0 : 200}
                                title={item.productName}
                                description={item.story}
                                linkTo={item.productPath || ""}
                              />
                            </Box>
                          );
                        })}
                      </Carousel>
                    </Section>
                  </Box>
                )}
                <Link href="/interest-form">

                  <Box mb={4} className={classes.interest}>
                    <Box>
                      {isMobile ? (
                        <Text component="div" color="white" variant="body1" weight="600">
                          {t("interest.title")}
                        </Text>
                      ) : (
                        <Typography fontWeight="bold" fontSize="16px" mb="10px">
                          {t("interest.title")}
                        </Typography>
                      )}
                      {isMobile ? null : (
                        <Text color="white" variant="caption1">
                          {t("interest.desc")}
                        </Text>
                      )}
                    </Box>
                    <ChevronRight sx={{ width: 35, height: 35 }} />
                  </Box>

                </Link>
                {isMobile && insightList?.data?.length ? (
                  <Divider sx={{ borderColor: "#DEE3ED", marginBottom: "24px" }} />
                ) : null}
              </Grid>
              <Grid item xs={12} lg={5}>
                <Box mb={3}>
                  {insightList?.data?.length > 0 && (
                    <Text variant="h1" weight="700">
                      {t("insight.relate")}
                    </Text>
                  )}
                </Box>
                {relatedArticle}
              </Grid>
            </Grid>
          </Container>
        </Box>
        {isMobile ? null : <Footer dataNavbar={dataNavbar} />}
      </Box>
    </>
  );
};
